// src/pages/supplier/RfqPage.jsx
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { supplierRfqApi } from '../../api/services'
import { Card, CardHeader, CardBody, Button, StatusBadge, PageSpinner, Badge, Empty } from '../../components/common/ui'
import { BarChart2, Award, Clock, TrendingUp, ChevronDown, ChevronUp, Send, MessageSquare, Eye, Calendar, MapPin, Filter, X } from 'lucide-react'
import toast from 'react-hot-toast'

const FABRIC_FILTERS = ['All', 'Cotton', 'Silk', 'Linen', 'Wool', 'Polyester', 'Rayon', 'Denim', 'Chiffon', 'Georgette', 'Satin']

const TABS = [
  { key: 'board',     label: 'RFQ Board',    icon: Eye },
  { key: 'my_quotes', label: 'My Quotes',    icon: Send },
  { key: 'won',       label: 'Won',          icon: Award },
]

export default function SupplierRfqPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('board')
  const [fabricFilter, setFabricFilter] = useState('All')
  const [expandedRfq, setExpandedRfq] = useState(null)
  const [quoteForm, setQuoteForm] = useState({})
  const [quoting, setQuoting] = useState(null)
  const [counteringId, setCounteringId] = useState(null)
  const [counterForm, setCounterForm] = useState({ price_per_metre: '', lead_time: '', notes: '' })
  const [showFilters, setShowFilters] = useState(false)

  // Dashboard data (metrics + board + recent quotes)
  const { data, isLoading } = useQuery({
    queryKey: ['supplier-rfq-dashboard'],
    queryFn: () => supplierRfqApi.dashboard().then(r => r.data?.data || r.data),
  })

  // Submit quote
  const submitQuote = useMutation({
    mutationFn: (rfqId) => supplierRfqApi.submitQuote(rfqId, quoteForm),
    onSuccess: () => {
      toast.success('Quote submitted!')
      qc.invalidateQueries({ queryKey: ['supplier-rfq-dashboard'] })
      setQuoting(null)
      setQuoteForm({})
    },
    onError: e => toast.error(e.response?.data?.message || 'Error submitting quote'),
  })

  // Counter-offer
  const counterMutation = useMutation({
    mutationFn: ({ rfqId, quoteId }) => supplierRfqApi.counterOffer(rfqId, quoteId, counterForm),
    onSuccess: () => {
      toast.success('Counter-offer sent!')
      qc.invalidateQueries({ queryKey: ['supplier-rfq-dashboard'] })
      setCounteringId(null)
      setCounterForm({ price_per_metre: '', lead_time: '', notes: '' })
    },
    onError: e => toast.error(e.response?.data?.message || 'Error sending counter-offer'),
  })

  if (isLoading) return <PageSpinner />

  const { metrics = {}, board = [], my_quotes = [] } = data || {}

  // Derived lists
  const filteredBoard = fabricFilter === 'All'
    ? board
    : board.filter(r => r.fabric_type?.toLowerCase() === fabricFilter.toLowerCase())

  const wonQuotes = my_quotes.filter(q => q.is_awarded)
  const pendingQuotes = my_quotes.filter(q => q.status === 'submitted' && !q.is_awarded)
  const rejectedQuotes = my_quotes.filter(q => q.status === 'rejected')

  return (
    <div className="max-w-5xl">
      <h1 className="text-xl font-display font-bold mb-6">RFQ & Quotations</h1>

      {/* ── Metrics ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Quotes Sent',   value: metrics.total_quoted || 0, icon: Send,       color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Won',           value: metrics.awarded || 0,      icon: Award,      color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Win Rate',      value: `${metrics.win_rate || 0}%`, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Value Won',     value: `₹${(metrics.total_value_won || 0).toLocaleString('en-IN')}`, icon: BarChart2, color: 'text-amber-600', bg: 'bg-amber-50' },
        ].map(k => (
          <div key={k.label} className={`${k.bg} border border-border rounded-2xl p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <k.icon size={14} className={k.color} />
              <span className="text-[10px] font-bold text-muted uppercase">{k.label}</span>
            </div>
            <p className={`text-xl font-extrabold ${k.color}`}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Response time banner */}
      {metrics.avg_response_hrs > 0 && (
        <div className="bg-surface rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs text-muted">
          <Clock size={12} />
          Avg response time: <strong className="text-gray-900">{metrics.avg_response_hrs}h</strong>
          {metrics.avg_response_hrs <= 12 && <span className="text-green-600 font-bold ml-1">Fast responder</span>}
          <span className="ml-auto text-[10px]">Pending: {metrics.pending || 0} · Rejected: {metrics.rejected || 0}</span>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 bg-surface rounded-xl p-1 mb-6 w-fit">
        {TABS.map(t => {
          const count = t.key === 'board' ? filteredBoard.length : t.key === 'my_quotes' ? my_quotes.length : wonQuotes.length
          const Icon = t.icon
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${tab === t.key ? 'bg-white shadow-sm text-gray-900' : 'text-muted hover:text-gray-700'}`}>
              <Icon size={13} />
              {t.label}
              {count > 0 && <span className="text-[10px] opacity-60">({count})</span>}
            </button>
          )
        })}
      </div>

      {/* ═════════════════════════════════════════════════════════════
         TAB: RFQ Board
         ════════════════════════════════════════════════════════════ */}
      {tab === 'board' && (
        <div>
          {/* Fabric filter bar */}
          <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1 text-xs font-bold text-muted hover:text-gray-700">
              <Filter size={12} /> Filter
            </button>
            <div className="h-4 w-px bg-border" />
            {FABRIC_FILTERS.slice(0, 7).map(f => (
              <button key={f} onClick={() => setFabricFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap transition-all ${fabricFilter === f ? 'bg-gray-900 text-white' : 'bg-surface text-muted hover:bg-gray-200'}`}>
                {f}
              </button>
            ))}
            {showFilters && FABRIC_FILTERS.slice(7).map(f => (
              <button key={f} onClick={() => setFabricFilter(f)}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap transition-all ${fabricFilter === f ? 'bg-gray-900 text-white' : 'bg-surface text-muted hover:bg-gray-200'}`}>
                {f}
              </button>
            ))}
          </div>

          {filteredBoard.length === 0 ? (
            <Empty icon="📋" title="No open RFQs" description={fabricFilter !== 'All' ? `No open RFQs for ${fabricFilter}. Try a different filter.` : 'No open RFQs right now. Check back soon!'} />
          ) : (
            <div className="space-y-3">
              {filteredBoard.map(rfq => {
                const isExpanded = expandedRfq === rfq.id
                const daysLeft = rfq.deadline ? Math.ceil((new Date(rfq.deadline) - new Date()) / 86400000) : null
                return (
                  <Card key={rfq.id} className="hover:shadow-md transition-shadow">
                    <CardBody>
                      {/* Header row */}
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedRfq(isExpanded ? null : rfq.id)}>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-sm">{rfq.title}</span>
                            <Badge className="bg-blue-50 text-blue-700 text-[10px]">{rfq.quotes_count || 0} quote{(rfq.quotes_count || 0) !== 1 ? 's' : ''}</Badge>
                          </div>
                          <p className="text-xs text-muted mt-1">{rfq.rfq_number}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-600 flex-wrap">
                            <span className="font-bold">{rfq.quantity_metres}m</span>
                            {rfq.fabric_type && <span className="bg-gray-100 px-2 py-0.5 rounded-full capitalize">{rfq.fabric_type}</span>}
                            {rfq.target_price_per_metre && <span>Target: ₹{rfq.target_price_per_metre}/m</span>}
                            {daysLeft !== null && (
                              <span className={`flex items-center gap-1 ${daysLeft <= 3 ? 'text-red-500 font-bold' : 'text-gray-500'}`}>
                                <Calendar size={11} />
                                {daysLeft <= 0 ? 'Expiring!' : `${daysLeft}d left`}
                              </span>
                            )}
                            {rfq.delivery_state && <span className="flex items-center gap-1"><MapPin size={11} /> {rfq.delivery_state}</span>}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button size="sm" onClick={() => {
                            setQuoting(quoting === rfq.id ? null : rfq.id)
                            setQuoteForm({ price_per_metre: '', lead_time: '', minimum_quantity: '', notes: '' })
                          }}>
                            <Send size={12} /> Quote
                          </Button>
                          <button onClick={() => setExpandedRfq(isExpanded ? null : rfq.id)} className="text-muted hover:text-gray-700">
                            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </button>
                        </div>
                      </div>

                      {/* Expanded details */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-border">
                          {rfq.description && <p className="text-sm text-gray-700 mb-3">{rfq.description}</p>}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            {rfq.gsm_range && <div className="bg-surface rounded-lg p-2"><span className="text-muted">GSM:</span> <span className="font-bold">{rfq.gsm_range}</span></div>}
                            {rfq.colour_preference && <div className="bg-surface rounded-lg p-2"><span className="text-muted">Colour:</span> <span className="font-bold">{rfq.colour_preference}</span></div>}
                            {rfq.weave_type && <div className="bg-surface rounded-lg p-2"><span className="text-muted">Weave:</span> <span className="font-bold capitalize">{rfq.weave_type}</span></div>}
                            {rfq.company_name && <div className="bg-surface rounded-lg p-2"><span className="text-muted">Company:</span> <span className="font-bold">{rfq.company_name}</span></div>}
                          </div>
                        </div>
                      )}

                      {/* Quote form */}
                      {quoting === rfq.id && (
                        <div className="mt-4 pt-4 border-t border-border">
                          <p className="text-xs font-bold text-gray-700 mb-2">Submit Your Quote</p>
                          <div className="grid sm:grid-cols-3 gap-2">
                            <input className="input" type="number" step="0.01" placeholder="Price per metre (₹) *"
                              value={quoteForm.price_per_metre || ''}
                              onChange={e => setQuoteForm(f => ({ ...f, price_per_metre: e.target.value }))} />
                            <input className="input" placeholder="Lead time (e.g. 7-10 days)"
                              value={quoteForm.lead_time || ''}
                              onChange={e => setQuoteForm(f => ({ ...f, lead_time: e.target.value }))} />
                            <input className="input" placeholder="Min quantity (optional)"
                              value={quoteForm.minimum_quantity || ''}
                              onChange={e => setQuoteForm(f => ({ ...f, minimum_quantity: e.target.value }))} />
                          </div>
                          <textarea className="input w-full h-16 mt-2" placeholder="Notes — fabric quality, certifications, special terms..."
                            value={quoteForm.notes || ''}
                            onChange={e => setQuoteForm(f => ({ ...f, notes: e.target.value }))} />

                          {/* Price guidance */}
                          {rfq.target_price_per_metre && quoteForm.price_per_metre && (
                            <div className="mt-2 text-xs">
                              {Number(quoteForm.price_per_metre) <= Number(rfq.target_price_per_metre) ? (
                                <span className="text-green-600 font-medium">Within buyer's target (₹{rfq.target_price_per_metre}/m)</span>
                              ) : (
                                <span className="text-amber-600 font-medium">
                                  {Math.round(((quoteForm.price_per_metre - rfq.target_price_per_metre) / rfq.target_price_per_metre) * 100)}% above buyer's target (₹{rfq.target_price_per_metre}/m)
                                </span>
                              )}
                            </div>
                          )}

                          <div className="flex gap-2 mt-3">
                            <Button size="sm" loading={submitQuote.isPending} onClick={() => submitQuote.mutate(rfq.id)} disabled={!quoteForm.price_per_metre}>
                              Submit Quote
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setQuoting(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════
         TAB: My Quotes
         ════════════════════════════════════════════════════════════ */}
      {tab === 'my_quotes' && (
        <div>
          {my_quotes.length === 0 ? (
            <Empty icon="📨" title="No quotes yet" description="Start quoting on open RFQs from the board." />
          ) : (
            <div className="space-y-3">
              {/* Pending quotes */}
              {pendingQuotes.length > 0 && (
                <>
                  <h2 className="text-xs font-bold text-muted uppercase tracking-wider">Pending ({pendingQuotes.length})</h2>
                  {pendingQuotes.map(q => (
                    <QuoteCard key={q.id} quote={q}
                      onCounter={() => { setCounteringId(counteringId === q.id ? null : q.id); setCounterForm({ price_per_metre: q.price_per_metre, lead_time: q.lead_time || '', notes: '' }) }}
                      counteringId={counteringId} counterForm={counterForm} setCounterForm={setCounterForm}
                      counterMutation={counterMutation} />
                  ))}
                </>
              )}

              {/* Awarded */}
              {wonQuotes.length > 0 && (
                <>
                  <h2 className="text-xs font-bold text-muted uppercase tracking-wider mt-6">Awarded ({wonQuotes.length})</h2>
                  {wonQuotes.map(q => <QuoteCard key={q.id} quote={q} />)}
                </>
              )}

              {/* Rejected */}
              {rejectedQuotes.length > 0 && (
                <>
                  <h2 className="text-xs font-bold text-muted uppercase tracking-wider mt-6">Rejected ({rejectedQuotes.length})</h2>
                  {rejectedQuotes.map(q => <QuoteCard key={q.id} quote={q} />)}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════
         TAB: Won
         ════════════════════════════════════════════════════════════ */}
      {tab === 'won' && (
        <div>
          {wonQuotes.length === 0 ? (
            <Empty icon="🏆" title="No won quotes yet" description="Keep quoting competitively on open RFQs to win orders." />
          ) : (
            <div className="space-y-3">
              {wonQuotes.map(q => (
                <Card key={q.id} className="border-green-200 bg-green-50/30">
                  <CardBody>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Award size={14} className="text-green-600" />
                          <span className="font-bold text-sm">{q.rfq?.title}</span>
                        </div>
                        <p className="text-xs text-muted mt-1">{q.rfq?.rfq_number} · {q.rfq?.buyer?.business_name || q.rfq?.buyer?.name}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="font-extrabold text-green-700">₹{Number(q.price_per_metre).toLocaleString('en-IN')}/m</span>
                          {q.total_quote && <span className="text-muted text-xs">Total: ₹{Number(q.total_quote).toLocaleString('en-IN')}</span>}
                          {q.rfq?.quantity_metres && <span className="text-xs text-gray-600">{q.rfq.quantity_metres}m</span>}
                          {q.rfq?.fabric_type && <span className="text-xs bg-green-100 px-2 py-0.5 rounded-full capitalize">{q.rfq.fabric_type}</span>}
                        </div>
                      </div>
                      <Badge className="bg-green-100 text-green-800">Won</Badge>
                    </div>
                  </CardBody>
                </Card>
              ))}

              {/* Won stats */}
              <div className="bg-surface rounded-xl p-4 text-center text-sm">
                <p className="text-muted">Total value won: <strong className="text-green-700">₹{(metrics.total_value_won || 0).toLocaleString('en-IN')}</strong></p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ── Quote Card (reusable for My Quotes tab) ───────────────────── */
function QuoteCard({ quote: q, onCounter, counteringId, counterForm, setCounterForm, counterMutation }) {
  return (
    <Card className={q.is_awarded ? 'border-green-200 bg-green-50/30' : q.status === 'rejected' ? 'opacity-60' : ''}>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm truncate">{q.rfq?.title}</span>
              <StatusBadge status={q.is_awarded ? 'awarded' : q.status} />
              {q.revision_number > 1 && <span className="text-[10px] text-blue-500">Rev #{q.revision_number}</span>}
            </div>
            <p className="text-xs text-muted mt-1">
              {q.rfq?.rfq_number}
              {q.rfq?.buyer?.business_name && ` · ${q.rfq.buyer.business_name}`}
              {q.rfq?.buyer?.name && !q.rfq?.buyer?.business_name && ` · ${q.rfq.buyer.name}`}
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className="font-extrabold">₹{Number(q.price_per_metre).toLocaleString('en-IN')}/m</span>
              {q.total_quote && <span className="text-xs text-muted">Total: ₹{Number(q.total_quote).toLocaleString('en-IN')}</span>}
              {q.lead_time && <span className="text-xs text-gray-600">{q.lead_time}</span>}
            </div>
            {q.notes && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{q.notes}</p>}

            {/* RFQ details summary */}
            <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-500 flex-wrap">
              {q.rfq?.quantity_metres && <span>{q.rfq.quantity_metres}m</span>}
              {q.rfq?.fabric_type && <span className="bg-gray-100 px-1.5 py-0.5 rounded capitalize">{q.rfq.fabric_type}</span>}
              {q.rfq?.deadline && (
                <span className="flex items-center gap-1"><Calendar size={10} /> {new Date(q.rfq.deadline).toLocaleDateString('en-IN')}</span>
              )}
            </div>
          </div>

          {/* Counter-offer action for pending quotes */}
          {q.status === 'submitted' && !q.is_awarded && onCounter && (
            <Button size="sm" variant="ghost" onClick={onCounter}>
              <MessageSquare size={12} /> Revise
            </Button>
          )}
        </div>

        {/* Counter-offer form */}
        {counteringId === q.id && counterForm && setCounterForm && (
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <p className="text-xs font-bold text-gray-700">Revise Your Quote</p>
            <div className="grid sm:grid-cols-2 gap-2">
              <input className="input text-sm" type="number" step="0.01" placeholder="Revised price/m *"
                value={counterForm.price_per_metre}
                onChange={e => setCounterForm(f => ({ ...f, price_per_metre: e.target.value }))} />
              <input className="input text-sm" placeholder="Lead time"
                value={counterForm.lead_time}
                onChange={e => setCounterForm(f => ({ ...f, lead_time: e.target.value }))} />
            </div>
            <textarea className="input w-full text-sm h-14" placeholder="Notes..."
              value={counterForm.notes}
              onChange={e => setCounterForm(f => ({ ...f, notes: e.target.value }))} />
            <div className="flex gap-2">
              <Button size="sm" loading={counterMutation?.isPending}
                disabled={!counterForm.price_per_metre}
                onClick={() => counterMutation?.mutate({ rfqId: q.rfq_id, quoteId: q.id })}>
                Send Revision
              </Button>
              <Button size="sm" variant="ghost" onClick={onCounter}>Cancel</Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
